import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PdfUploader from "@/components/PdfUploader";
import { cvAPI, exportAPI } from "@/lib/api";
import { PenTool, Loader2, FileDown, CheckCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const BANDS = ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b"];
const MODES = [
  { value: "full", label: "Full CV Rewrite" },
  { value: "sections", label: "Rewrite Selected Sections" },
  { value: "optimize", label: "Optimise for Specific Role" },
];

export default function CVRewriter() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    cv_text: "", target_role: "", target_band: "", rewrite_mode: "full",
  });

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleRewrite = async () => {
    if (!form.cv_text.trim()) {
      toast.error("Please paste your CV");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await cvAPI.rewrite(form);
      setResult(res.data);
      toast.success("CV rewritten successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rewriting failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!result?.rewritten_cv) return;
    try {
      const res = await exportAPI.docx({ content: result.rewritten_cv, title: "Rewritten CV" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Rewritten_CV.docx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CV downloaded!");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cv-rewriter-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>CV Rewriter</h1>
          <p className="text-slate-500 mt-1">Transform your CV into an achievement-focused, STAR-formatted document.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Your CV</CardTitle>
                    <CardDescription>Paste your current CV content or upload PDF</CardDescription>
                  </div>
                  <PdfUploader onTextExtracted={(text) => updateForm("cv_text", text)} label="Upload CV (PDF or DOCX)" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Paste your CV content here..."
                  className="min-h-[300px]"
                  value={form.cv_text}
                  onChange={(e) => updateForm("cv_text", e.target.value)}
                  data-testid="cv-input"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Rewrite Mode</Label>
                    <Select value={form.rewrite_mode} onValueChange={(v) => updateForm("rewrite_mode", v)}>
                      <SelectTrigger data-testid="cv-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Target Band</Label>
                    <Select value={form.target_band} onValueChange={(v) => updateForm("target_band", v)}>
                      <SelectTrigger data-testid="cv-band-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>{BANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Target Role (optional)</Label>
                  <Input placeholder="e.g. Band 7 Ward Manager" value={form.target_role} onChange={(e) => updateForm("target_role", e.target.value)} data-testid="cv-role-input" />
                </div>
                <Button className="w-full bg-[#003087] text-white hover:bg-[#002366]" onClick={handleRewrite} disabled={loading} data-testid="cv-rewrite-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rewriting...</> : <><PenTool className="w-4 h-4 mr-2" /> Rewrite CV</>}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="space-y-4">
            {loading && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-10 text-center">
                  <Loader2 className="w-10 h-10 text-[#003087] animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Rewriting your CV...</p>
                </CardContent>
              </Card>
            )}

            {result && !result.error && (
              <>
                {result.improvement_score && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="cv-score-card">
                    <CardContent className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-[#00A3A6]" />
                        <span className="font-medium text-slate-900">Improvement Score</span>
                      </div>
                      <span className="text-2xl font-bold text-[#007F3B]">{result.improvement_score}%</span>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="cv-result-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Rewritten CV</CardTitle>
                      <Button size="sm" className="bg-[#003087] text-white hover:bg-[#002366]" onClick={handleExport} data-testid="cv-export-btn">
                        <FileDown className="w-4 h-4 mr-1" /> Export .docx
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none text-sm text-slate-700" data-testid="cv-rewritten-text">
                      <ReactMarkdown>{result.rewritten_cv}</ReactMarkdown>
                    </div>
                  </CardContent>
                </Card>

                {result.changes_made?.length > 0 && (
                  <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                    <CardHeader className="pb-2"><CardTitle className="text-base">Changes Made</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {result.changes_made.map((c, i) => (
                          <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-[#007F3B] mt-0.5 flex-shrink-0" /> {c}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {!result && !loading && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                <CardContent className="p-10 text-center">
                  <PenTool className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Paste your CV and click Rewrite to get an improved version.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
